import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiTrash2, FiMail, FiUser } from 'react-icons/fi';
import { useQuery, useMutation, gql } from '@apollo/client';
import { getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

// GraphQL queries and mutations
const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      phoneNumber
      permissions
    }
  }
`;

const GET_EMAIL_ADDRESSES = gql`
  query GetEmailAddresses {
    emailAddresses {
      id
      email
      name
      type
      associatedClients
      emailsSent
      emailsReceived
      lastSeenAt
    }
  }
`;

const ASSIGN_EMAIL_TO_CLIENT = gql`
  mutation AssignEmailToClient($emailId: String!, $clientId: String!) {
    assignEmailToClient(emailId: $emailId, clientId: $clientId) {
      id
      associatedClients
    }
  }
`;

const UNASSIGN_EMAIL_FROM_CLIENT = gql`
  mutation UnassignEmailFromClient($emailId: String!, $clientId: String!) {
    unassignEmailFromClient(emailId: $emailId, clientId: $clientId) {
      id
      associatedClients
    }
  }
`;

interface Client {
  id: string;
  fName: string;
  lName: string;
  email: string;
  phoneNumber: string;
  permissions: string[];
}

interface EmailAddress {
  id: string;
  email: string;
  name?: string;
  type: string;
  associatedClients: string[];  // Just client IDs
  emailsSent: number;
  emailsReceived: number;
  lastSeenAt: string;
}

export const EmailAccountsAdmin: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [selectedEmail, setSelectedEmail] = useState<EmailAddress | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // GraphQL queries
  const { data: clientsData, loading: clientsLoading } = useQuery(GET_CLIENTS);
  const { data: emailsData, loading: emailsLoading, refetch: refetchEmails } = useQuery(GET_EMAIL_ADDRESSES);
  
  // GraphQL mutations
  const [assignEmail] = useMutation(ASSIGN_EMAIL_TO_CLIENT, {
    onCompleted: () => {
      toast({
        title: 'Email assigned successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetchEmails();
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error assigning email',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const [unassignEmail] = useMutation(UNASSIGN_EMAIL_FROM_CLIENT, {
    onCompleted: () => {
      toast({
        title: 'Email unassigned successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      refetchEmails();
    },
    onError: (error) => {
      toast({
        title: 'Error unassigning email',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const bg = getColor("background.main");
  const cardGradientBg = getColor("background.cardGradient");
  const cardBorder = getColor("border.darkCard");
  // Use brighter colors for dark theme
  const textColor = "white";
  const mutedTextColor = "gray.300";
  const labelColor = "gray.400";

  const clients: Client[] = clientsData?.clients || [];
  const emailAddresses: EmailAddress[] = emailsData?.emailAddresses || [];

  // Filter email addresses based on search and type
  const filteredEmails = emailAddresses.filter(email => {
    const matchesSearch = email.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || email.type === filterType;
    return matchesSearch && matchesType;
  });

  const handleAssignEmail = async (clientId: string) => {
    if (!selectedEmail) return;
    
    await assignEmail({
      variables: {
        emailId: selectedEmail.id,
        clientId: clientId,
      },
    });
  };

  const handleUnassignEmail = async (emailId: string, clientId: string) => {
    await unassignEmail({
      variables: {
        emailId,
        clientId,
      },
    });
  };

  const openAssignModal = (email: EmailAddress) => {
    setSelectedEmail(email);
    onOpen();
  };

  if (clientsLoading || emailsLoading) {
    return (
      <Box minH="100vh" bg={bg} p={6} display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.400" />
          <Text color={textColor}>Loading email accounts...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bg}>
      <NavbarWithCallToAction />
      <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW={{ base: "100%", lg: "none" }}>
        {/* Header */}
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} mb={2}>
            📧 Email Account Management
          </Text>
          <Text color={mutedTextColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.6">
            Control which emails your clients can access. Assign specific email addresses to individual clients - they'll only see emails sent to their assigned addresses when they visit their inbox.
          </Text>
        </Box>

        {/* Stats Cards */}
        <HStack spacing={{ base: 2, md: 4 }} direction={{ base: "column", md: "row" }} align="stretch">
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
            flex={1}
          >
            <CardBody p={{ base: 4, md: 6 }}>
              <HStack justify="space-between">
                <VStack align="start" spacing={1} flex="1">
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">Tracked Email Addresses</Text>
                  <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color={textColor}>
                    {emailAddresses.length}
                  </Text>
                  <Text fontSize="2xs" color="gray.400" display={{ base: "none", md: "block" }}>
                    Auto-detected from incoming emails
                  </Text>
                </VStack>
                <Box p={{ base: 2, md: 3 }} borderRadius="lg" bg="blue.500" color="white">
                  <FiMail size={24} />
                </Box>
              </HStack>
            </CardBody>
          </Card>

          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
            flex={1}
          >
            <CardBody p={{ base: 4, md: 6 }}>
              <HStack justify="space-between">
                <VStack align="start" spacing={1} flex="1">
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">Assigned to Clients</Text>
                  <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color={textColor}>
                    {emailAddresses.filter(e => e.associatedClients.length > 0).length}
                  </Text>
                  <Text fontSize="2xs" color="gray.400" display={{ base: "none", md: "block" }}>
                    Addresses with client access
                  </Text>
                </VStack>
                <Box p={{ base: 2, md: 3 }} borderRadius="lg" bg="green.500" color="white">
                  <FiUser size={24} />
                </Box>
              </HStack>
            </CardBody>
          </Card>

          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
            flex={1}
          >
            <CardBody p={{ base: 4, md: 6 }}>
              <HStack justify="space-between">
                <VStack align="start" spacing={1} flex="1">
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">Unassigned Addresses</Text>
                  <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color={textColor}>
                    {emailAddresses.filter(e => e.associatedClients.length === 0).length}
                  </Text>
                  <Text fontSize="2xs" color="gray.400" display={{ base: "none", md: "block" }}>
                    Need client assignment
                  </Text>
                </VStack>
                <Box p={{ base: 2, md: 3 }} borderRadius="lg" bg="orange.500" color="white">
                  <FiMail size={24} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        </HStack>

        {/* Filters */}
        <Card
          bg={cardGradientBg}
          backdropFilter="blur(10px)"
          boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
          border="1px solid"
          borderColor={cardBorder}
        >
          <CardBody p={{ base: 4, md: 6 }}>
            <VStack spacing={4} align="stretch">
              <Input
                placeholder="Search email addresses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor={cardBorder}
                color={textColor}
                _placeholder={{ color: "gray.500" }}
              />
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor={cardBorder}
                color={textColor}
                maxW={{ base: "100%", md: "200px" }}
              >
                <option value="all">All Types</option>
                <option value="PERSONAL">Personal</option>
                <option value="BUSINESS">Business</option>
                <option value="SUPPORT">Support</option>
                <option value="MARKETING">Marketing</option>
                <option value="NOREPLY">No Reply</option>
              </Select>
            </VStack>
          </CardBody>
        </Card>

        {/* Email Addresses Table */}
        <Card
          bg={cardGradientBg}
          backdropFilter="blur(10px)"
          boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
          border="1px solid"
          borderColor={cardBorder}
        >
          <CardBody p={{ base: 4, md: 6 }}>
            <Box overflowX="auto" width="100%">
              <Table variant="simple" size={{ base: "sm", md: "md" }} minWidth="700px">
                <Thead position="sticky" top={0} bg={cardGradientBg}>
                  <Tr>
                    <Th color={labelColor} px={{ base: 2, md: 4 }} fontSize={{ base: "xs", md: "sm" }} minW="180px">Email Address</Th>
                    <Th color={labelColor} px={{ base: 2, md: 4 }} fontSize={{ base: "xs", md: "sm" }} minW="80px">Type</Th>
                    <Th color={labelColor} px={{ base: 2, md: 4 }} fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", md: "table-cell" }} minW="150px">Assigned Clients</Th>
                    <Th color={labelColor} px={{ base: 2, md: 4 }} fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", lg: "table-cell" }} minW="100px">Activity</Th>
                    <Th color={labelColor} px={{ base: 2, md: 4 }} fontSize={{ base: "xs", md: "sm" }} display={{ base: "none", lg: "table-cell" }} minW="100px">Last Seen</Th>
                    <Th color={labelColor} px={{ base: 2, md: 4 }} fontSize={{ base: "xs", md: "sm" }} minW="80px">Actions</Th>
                  </Tr>
                </Thead>
              <Tbody>
                {filteredEmails.map((email) => (
                  <Tr key={email.id}>
                    <Td px={{ base: 2, md: 4 }}>
                      <VStack align="start" spacing={1}>
                        <Text color={textColor} fontWeight="medium" fontSize={{ base: "xs", md: "sm" }} noOfLines={2}>
                          {email.email}
                        </Text>
                        {email.name && (
                          <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.300" noOfLines={1}>
                            {email.name}
                          </Text>
                        )}
                        <Text display={{ base: "block", md: "none" }} fontSize="2xs" color="gray.400">
                          {email.associatedClients.length > 0 ? `${email.associatedClients.length} client(s)` : "Unassigned"}
                        </Text>
                      </VStack>
                    </Td>
                    <Td px={{ base: 2, md: 4 }}>
                      <Badge
                        colorScheme={
                          email.type === 'PERSONAL' ? 'blue' :
                          email.type === 'BUSINESS' ? 'green' :
                          email.type === 'SUPPORT' ? 'yellow' :
                          email.type === 'MARKETING' ? 'purple' :
                          'gray'
                        }
                        variant="subtle"
                        fontSize={{ base: "2xs", md: "xs" }}
                      >
                        {email.type === 'PERSONAL' ? 'PERS' : email.type === 'BUSINESS' ? 'BIZ' : email.type === 'MARKETING' ? 'MKTG' : email.type}
                      </Badge>
                    </Td>
                    <Td display={{ base: "none", md: "table-cell" }} px={{ base: 2, md: 4 }}>
                      <VStack align="start" spacing={1} maxW="150px">
                        {email.associatedClients.length === 0 ? (
                          <Text fontSize="sm" color="gray.400">
                            Unassigned
                          </Text>
                        ) : (
                          email.associatedClients.map((clientId) => {
                            const client = clients.find(c => c.id === clientId);
                            return (
                              <HStack key={clientId} spacing={2}>
                                <Badge colorScheme="green" variant="subtle" fontSize="2xs">
                                  {client ? `${client.fName} ${client.lName}` : clientId}
                                </Badge>
                                <IconButton
                                  aria-label="Unassign client"
                                  icon={<FiTrash2 />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => handleUnassignEmail(email.id, clientId)}
                                />
                              </HStack>
                            );
                          })
                        )}
                      </VStack>
                    </Td>
                    <Td display={{ base: "none", lg: "table-cell" }} px={{ base: 2, md: 4 }}>
                      <VStack align="start" spacing={1}>
                        <Text fontSize="xs" color={textColor}>
                          📤 {email.emailsSent} sent
                        </Text>
                        <Text fontSize="xs" color={textColor}>
                          📥 {email.emailsReceived} received
                        </Text>
                      </VStack>
                    </Td>
                    <Td display={{ base: "none", lg: "table-cell" }} px={{ base: 2, md: 4 }}>
                      <Text fontSize="xs" color="gray.300">
                        {new Date(email.lastSeenAt).toLocaleDateString()}
                      </Text>
                    </Td>
                    <Td px={{ base: 2, md: 4 }}>
                      <Tooltip label="Assign to client">
                        <IconButton
                          aria-label="Assign email"
                          icon={<FiPlus />}
                          size={{ base: "xs", md: "sm" }}
                          variant="ghost"
                          colorScheme="blue"
                          minW={{ base: "28px", md: "32px" }}
                          onClick={() => openAssignModal(email)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>

        {/* How It Works Info Box */}
        <Card
          bg={cardGradientBg}
          backdropFilter="blur(10px)"
          boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
          border="1px solid"
          borderColor={cardBorder}
        >
          <CardBody p={{ base: 4, md: 6 }}>
            <VStack align="start" spacing={{ base: 2, md: 3 }}>
              <HStack>
                <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color={textColor}>
                  📚 How Email Assignment Works
                </Text>
              </HStack>
              <VStack align="start" spacing={{ base: 1, md: 2 }} pl={{ base: 2, md: 4 }}>
                <HStack align="start" spacing={{ base: 2, md: 3 }}>
                  <Text color="blue.400" fontWeight="bold">1.</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    Email addresses are automatically tracked when emails arrive at your Postmark inbound webhook
                  </Text>
                </HStack>
                <HStack align="start" spacing={{ base: 2, md: 3 }}>
                  <Text color="blue.400" fontWeight="bold">2.</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    Click the plus button next to any email address to assign it to specific clients
                  </Text>
                </HStack>
                <HStack align="start" spacing={{ base: 2, md: 3 }}>
                  <Text color="blue.400" fontWeight="bold">3.</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    Clients will only see emails from their assigned addresses in their inbox
                  </Text>
                </HStack>
                <HStack align="start" spacing={{ base: 2, md: 3 }}>
                  <Text color="blue.400" fontWeight="bold">4.</Text>
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    System admins always see all emails regardless of assignments
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Assignment Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "md" }}>
          <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <ModalContent 
            bg={cardGradientBg} 
            border="1px solid" 
            borderColor={cardBorder}
            margin={{ base: 0, md: "auto" }}
            borderRadius={{ base: 0, md: "md" }}
            maxH={{ base: "100vh", md: "90vh" }}
          >
            <ModalHeader color={textColor} fontSize={{ base: "lg", md: "xl" }}>
              Assign Email: {selectedEmail?.email}
            </ModalHeader>
            <ModalCloseButton color={textColor} />
            <ModalBody pb={6}>
              <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>
                  Select a client to assign this email address to:
                </Text>
                
                <VStack spacing={2} align="stretch" maxH={{ base: "300px", md: "400px" }} overflowY="auto">
                  {clients.map((client) => (
                    <HStack
                      key={client.id}
                      p={{ base: 3, md: 4 }}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={cardBorder}
                      justify="space-between"
                      bg="whiteAlpha.50"
                      direction={{ base: "column", md: "row" }}
                      align={{ base: "stretch", md: "center" }}
                      spacing={{ base: 2, md: 3 }}
                    >
                      <VStack align={{ base: "stretch", md: "start" }} spacing={1} flex="1">
                        <Text color={textColor} fontWeight="medium" fontSize={{ base: "sm", md: "md" }} textAlign={{ base: "center", md: "left" }}>
                          {client.fName} {client.lName}
                        </Text>
                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400" textAlign={{ base: "center", md: "left" }}>
                          {client.email}
                        </Text>
                      </VStack>
                      <Button
                        size={{ base: "sm", md: "sm" }}
                        colorScheme="blue"
                        width={{ base: "100%", md: "auto" }}
                        minW={{ md: "120px" }}
                        onClick={() => handleAssignEmail(client.id)}
                        isDisabled={selectedEmail?.associatedClients.includes(client.id)}
                      >
                        {selectedEmail?.associatedClients.includes(client.id) ? 'Already Assigned' : 'Assign'}
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
      </Box>
      <FooterWithFourColumns />
    </Box>
  );
};